import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import SignatureCanvas from '@/components/SignatureCanvas.vue';
import DocumentSignDialog from '@/components/DocumentSignDialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import { VALIDATION_RULES } from '@/constants/ValidationRules';
import { buildPlaceholderValues, renderPreview, usedPlaceholders } from '@/constants/ContractPlaceholders';
import { getSeasonYear } from '@/store/seasonYear';
let instance = null;
let ContractSignDialog = class ContractSignDialog extends Vue {
    open = false;
    step = 1;
    signing = false;
    signature = null;
    profile = { address: '', birthNumber: '', ico: '' };
    rules = VALIDATION_RULES;
    targetDate = '';
    targetTemplateId = '';
    /** date is the day the contract has to cover, YYYY-MM-DD. */
    static show(date, templateId) {
        if (!instance)
            return;
        instance.targetDate = date;
        instance.targetTemplateId = templateId;
        instance.reset();
        instance.open = true;
    }
    created() {
        instance = this;
    }
    beforeDestroy() {
        if (instance === this)
            instance = null;
    }
    get instructor() {
        return this.$store.getters['instructor/get'](this.$store.getters['user/userId']);
    }
    get template() {
        return this.$store.getters['contract/templates']
            .find((t) => t.id === this.targetTemplateId);
    }
    /** The template body decides which identifiers have to be filled in, same as the callable. */
    get missing() {
        const data = this.instructor?.data;
        const used = usedPlaceholders(this.template?.data.bodyHtml ?? '');
        return {
            address: !data?.address,
            birthNumber: used.has('birthNumber') && !data?.birthNumber,
            ico: used.has('ico') && !data?.ico,
        };
    }
    get grade() {
        return this.$store.getters['grade/all'].find((g) => g.id === this.instructor?.data.grade_id);
    }
    get previewHtml() {
        const data = this.instructor?.data;
        if (!this.template || !data)
            return '';
        const values = buildPlaceholderValues({
            ...data,
            address: data.address || this.profile.address,
            birthNumber: data.birthNumber || this.profile.birthNumber,
            ico: data.ico || this.profile.ico,
        }, this.grade?.data ?? null, getSeasonYear(), this.template.data);
        return renderPreview(this.template.data.bodyHtml, values);
    }
    reset() {
        this.signature = null;
        this.signing = false;
        this.profile = { address: '', birthNumber: '', ico: '' };
        const m = this.missing;
        this.step = m.address || m.birthNumber || m.ico ? 1 : 2;
    }
    /** The preview marks where the instructor signs; clicking it brings the canvas into view. */
    onPreviewClick(event) {
        if (!event.target.closest('[data-placeholder="signatureInstructor"]'))
            return;
        const canvas = this.$refs.canvas;
        canvas.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        canvas.highlight();
    }
    continueToPreview() {
        if (!this.$refs.profileForm.validate())
            return;
        this.step = 2;
    }
    async sign() {
        if (!this.template || !this.signature || this.signing)
            return;
        this.signing = true;
        try {
            await this.$store.dispatch('contract/sign', {
                templateId: this.template.id,
                signaturePng: this.signature,
                ...(this.missing.address ? { address: this.profile.address } : {}),
                ...(this.missing.birthNumber ? { birthNumber: this.profile.birthNumber } : {}),
                ...(this.missing.ico ? { ico: this.profile.ico } : {}),
            });
            Snackbar.success('Smlouva byla podepsána');
            this.open = false;
            this.promptDocument();
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Podpis smlouvy se nezdařil');
        }
        finally {
            this.signing = false;
        }
    }
    /** A fresh contract may bring documents with it; the instructor decides on the first undecided one. */
    promptDocument() {
        const userId = this.$store.getters['user/userId'];
        for (const key of this.instructor?.data.templateKeys ?? []) {
            const template = this.$store.getters['document/templateFor'](key, this.targetDate);
            if (!template)
                continue;
            if (this.$store.getters['document/decisionFor'](userId, key, this.targetDate))
                continue;
            DocumentSignDialog.show(this.targetDate, template.id);
            return;
        }
    }
    onOpenChange(value) {
        if (!value)
            this.$emit('closed');
    }
};
__decorate([
    Watch('open')
], ContractSignDialog.prototype, "onOpenChange", null);
ContractSignDialog = __decorate([
    Component({ components: { SignatureCanvas } })
], ContractSignDialog);
export default ContractSignDialog;
